import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FileCode2, Download, RefreshCw, Copy, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "../../lib/api";
import { track } from "../../lib/analytics";

/**
 * M5 — G-code skeleton generator card.
 *
 * Two modes:
 * - When `quoteId` is provided → calls /guardrail/generate-gcode with {quote_id}
 *   and pulls operations/material/dimensions from the saved quote.
 * - Otherwise → renders a small operations editor and lets the user generate
 *   a stand-alone skeleton.
 */
export default function GcodeSkeletonCard({ quoteId = null, defaultMaterial = "", defaultDimensions = null }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  // Manual mode only — operations editor
  const [opsText, setOpsText] = useState("facing\ndrilling\ntapping");
  const [material, setMaterial] = useState(defaultMaterial);
  const [length, setLength] = useState(defaultDimensions?.length_mm || 100);
  const [width, setWidth] = useState(defaultDimensions?.width_mm || 80);
  const [height, setHeight] = useState(defaultDimensions?.height_mm || 25);
  const [programNumber, setProgramNumber] = useState(1000);
  const [partDescription, setPartDescription] = useState("");

  const generate = async () => {
    setLoading(true);
    setError("");
    setResult(null);
    setCopied(false);
    try {
      let payload;
      if (quoteId) {
        payload = { quote_id: quoteId, options: { program_number: parseInt(programNumber, 10) || 1000 } };
      } else {
        const ops = opsText
          .split(/[\n,]/)
          .map((s) => s.trim())
          .filter(Boolean)
          .map((t) => ({ type: t }));
        if (ops.length === 0) {
          setError(t("gcodeCard.needOneOp"));
          setLoading(false);
          return;
        }
        payload = {
          operations: ops,
          material,
          dimensions: {
            length_mm: parseFloat(length) || 100,
            width_mm: parseFloat(width) || 80,
            height_mm: parseFloat(height) || 25,
          },
          part_description: partDescription || undefined,
          options: { program_number: parseInt(programNumber, 10) || 1000 },
        };
      }
      const { data } = await api.post("/guardrail/generate-gcode", payload, { timeout: 60000 });
      setResult(data);
      track("gcode_skeleton_generated", {
        source: quoteId ? "quote" : "manual",
        operations_count: data.summary?.operations_count || 0,
        lines: data.summary?.lines_emitted || 0,
      });
    } catch (e) {
      setError(e.response?.data?.detail || t("gcodeCard.genError"));
      track("gcode_skeleton_failed", { reason: e.response?.status || "network" });
    }
    setLoading(false);
  };

  const downloadFile = () => {
    if (!result?.gcode) return;
    const blob = new Blob([result.gcode], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const pn = result.summary?.program_number || `O${programNumber}`;
    a.download = `${pn}.nc`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    track("gcode_skeleton_downloaded", { filename: a.download });
  };

  const copyToClipboard = async () => {
    if (!result?.gcode) return;
    try {
      await navigator.clipboard.writeText(result.gcode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      setError(t("gcodeCard.clipboardError"));
    }
  };

  return (
    <div className="tech-card p-5 border-violet-500/30" data-testid="gcode-skeleton-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-violet-300">
          <FileCode2 size={14}/>
          <div className="label-tech text-violet-300">G-code skeleton · M5</div>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={loading}
          className="px-3 py-1.5 rounded-sm bg-violet-500/10 border border-violet-500/40 text-violet-200 hover:bg-violet-500/20 hover:text-violet-100 disabled:opacity-50 text-xs font-mono uppercase tracking-wider flex items-center gap-2"
          data-testid="gcode-generate-btn"
        >
          {loading ? <RefreshCw size={12} className="animate-spin"/> : <FileCode2 size={12}/>}
          {loading ? t("gcodeCard.generating") : (result ? t("gcodeCard.regenerate") : t("gcodeCard.generate"))}
        </button>
      </div>

      <p className="text-xs text-slate-400 mb-3 leading-relaxed">
        {quoteId
          ? t("gcodeCard.introQuote")
          : t("gcodeCard.introManual")}
      </p>

      {!quoteId && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4 text-sm">
          <div>
            <label htmlFor="gcode-ops" className="label-tech text-[10px] block mb-1">{t("gcodeCard.opsLabel")}</label>
            <textarea
              id="gcode-ops"
              value={opsText}
              onChange={(e) => setOpsText(e.target.value)}
              rows={4}
              placeholder="facing&#10;drilling&#10;tapping"
              className="w-full px-2 py-1.5 rounded-sm bg-slate-900 border border-slate-700 font-mono-data text-slate-200 focus:border-violet-500 focus:outline-none text-xs"
              data-testid="gcode-ops-input"
            />
            <div className="text-[10px] text-slate-500 mt-1 font-mono-data">
              {t("gcodeCard.typesHint")}
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <label htmlFor="gcode-material" className="label-tech text-[10px] block mb-1">{t("gcodeCard.material")}</label>
              <input
                id="gcode-material"
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
                placeholder={t("gcodeCard.materialPh")}
                className="w-full px-2 py-1.5 rounded-sm bg-slate-900 border border-slate-700 font-mono-data text-slate-200 focus:border-violet-500 focus:outline-none text-xs"
                data-testid="gcode-material-input"
              />
            </div>
            <div>
              <label htmlFor="gcode-part-desc" className="label-tech text-[10px] block mb-1">{t("gcodeCard.partDesc")}</label>
              <input
                id="gcode-part-desc"
                value={partDescription}
                onChange={(e) => setPartDescription(e.target.value)}
                placeholder={t("gcodeCard.partDescPh")}
                className="w-full px-2 py-1.5 rounded-sm bg-slate-900 border border-slate-700 font-mono-data text-slate-200 focus:border-violet-500 focus:outline-none text-xs"
                data-testid="gcode-partdesc-input"
              />
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              <div>
                <label htmlFor="gcode-length" className="label-tech text-[10px] block mb-1">L (mm)</label>
                <input id="gcode-length" type="number" min="1" value={length} onChange={(e) => setLength(e.target.value)} className="w-full px-2 py-1 rounded-sm bg-slate-900 border border-slate-700 font-mono-data text-slate-200 focus:border-violet-500 focus:outline-none text-xs text-right" data-testid="gcode-length-input"/>
              </div>
              <div>
                <label htmlFor="gcode-width" className="label-tech text-[10px] block mb-1">W (mm)</label>
                <input id="gcode-width" type="number" min="1" value={width} onChange={(e) => setWidth(e.target.value)} className="w-full px-2 py-1 rounded-sm bg-slate-900 border border-slate-700 font-mono-data text-slate-200 focus:border-violet-500 focus:outline-none text-xs text-right" data-testid="gcode-width-input"/>
              </div>
              <div>
                <label htmlFor="gcode-height" className="label-tech text-[10px] block mb-1">H (mm)</label>
                <input id="gcode-height" type="number" min="1" value={height} onChange={(e) => setHeight(e.target.value)} className="w-full px-2 py-1 rounded-sm bg-slate-900 border border-slate-700 font-mono-data text-slate-200 focus:border-violet-500 focus:outline-none text-xs text-right" data-testid="gcode-height-input"/>
              </div>
              <div>
                <label htmlFor="gcode-prognum" className="label-tech text-[10px] block mb-1">O####</label>
                <input id="gcode-prognum" type="number" min="1" max="9999" value={programNumber} onChange={(e) => setProgramNumber(e.target.value)} className="w-full px-2 py-1 rounded-sm bg-slate-900 border border-slate-700 font-mono-data text-slate-200 focus:border-violet-500 focus:outline-none text-xs text-right" data-testid="gcode-prognum-input"/>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-3 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-sm px-3 py-2" data-testid="gcode-error">
          {error}
        </div>
      )}

      {result && (
        <div className="space-y-3" data-testid="gcode-result">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono-data">
            <Tile label={t("gcodeCard.program")} value={result.summary?.program_number || "—"}/>
            <Tile label={t("gcodeCard.operations")} value={result.summary?.operations_count || 0}/>
            <Tile label={t("gcodeCard.tools")} value={(result.summary?.tools_used || []).length}/>
            <Tile label={t("gcodeCard.lines")} value={result.summary?.lines_emitted || 0}/>
          </div>

          <div className="relative">
            <pre
              className="bg-slate-950 border border-slate-800 rounded-sm p-3 text-[11px] font-mono-data text-slate-200 overflow-auto max-h-[420px] whitespace-pre leading-relaxed"
              data-testid="gcode-output"
            >{result.gcode}</pre>
            <div className="absolute top-2 right-2 flex gap-1.5">
              <button
                type="button"
                onClick={copyToClipboard}
                className="px-2 py-1 rounded-sm bg-slate-800 border border-slate-700 text-slate-300 hover:border-violet-500 hover:text-violet-200 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1"
                data-testid="gcode-copy-btn"
              >
                {copied ? <><CheckCircle2 size={10}/> {t("gcodeCard.copied")}</> : <><Copy size={10}/> {t("gcodeCard.copy")}</>}
              </button>
              <button
                type="button"
                onClick={downloadFile}
                className="px-2 py-1 rounded-sm bg-violet-500/10 border border-violet-500/40 text-violet-200 hover:bg-violet-500/20 text-[10px] font-mono uppercase tracking-wider flex items-center gap-1"
                data-testid="gcode-download-btn"
              >
                <Download size={10}/> .nc
              </button>
            </div>
          </div>

          {(result.summary?.warnings || []).length > 0 && (
            <div className="border border-amber-500/30 bg-amber-500/[0.06] rounded-sm px-3 py-2" data-testid="gcode-warnings">
              <div className="flex items-center gap-2 mb-1 text-amber-300 text-[10px] font-mono uppercase tracking-wider">
                <AlertTriangle size={11}/> {t("gcodeCard.manualReview")}
              </div>
              <ul className="space-y-1 text-[11px] text-amber-100/90 leading-relaxed">
                {result.summary.warnings.map((w, i) => (
                  <li key={`gw-${i}`} className="flex items-start gap-2">
                    <span className="text-amber-400/70">›</span>
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value }) {
  return (
    <div className="border border-slate-800 bg-slate-900/40 rounded-sm px-2 py-1.5">
      <div className="label-tech text-[9px] text-slate-500 mb-0.5">{label}</div>
      <div className="text-slate-100 font-mono-data">{value}</div>
    </div>
  );
}
